/**
 * Motor de reglas de la iteración 3 — crear experiencia (documentos 03a y 03b, y el
 * enlace de organizador que se adelanta desde el documento 04). Funciones puras, sin
 * estado ni red, sin importar Nest, Prisma ni React (ADR-0017): las consumen el
 * navegador —para decidir qué puerta o qué control mostrar— y el servidor —para decidir
 * qué aceptar—, y es lo que hace posible la prueba de contrato entre pantalla y servidor
 * que exige el documento 03a (§13: "hoy coinciden por suerte; nada lo comprueba").
 */

import { type MapaVisibilidad } from './visibility';

// -----------------------------------------------------------------------------
// La reserva de plazas (documento 03a §5.2, RN-6 y §8; CA-05a.8 del documento 05a, más
// específico que CA-03a.8 para el valor efectivo).
// -----------------------------------------------------------------------------

/** Suelo de la reserva de plazas para quien no ha vivido ninguna experiencia: un tercio. */
export const RESERVA_SUELO_PCT = 33;

/** Techo de la reserva: el organizador puede subir hasta reservar el aforo entero. */
export const RESERVA_TECHO_PCT = 100;

/**
 * El valor **efectivo** de la reserva, nunca el guardado en bruto: la base guarda tal
 * cual lo que llega (incluso por debajo del suelo, o negativo) y este cálculo lo fija
 * dentro de `[RESERVA_SUELO_PCT, RESERVA_TECHO_PCT]` — CA-05a.8 del documento 05a, más
 * específico que CA-03a.8 ("el valor guardado o efectivo es 33"): "el suelo de la
 * reserva: se guarda lo que llega y el valor efectivo se calcula `clamp(33, 100)`".
 * RN-6: el organizador puede subirlo, nunca bajarlo del suelo.
 */
export const reservaEfectivaPct = (pct: number): number =>
	Math.min(RESERVA_TECHO_PCT, Math.max(RESERVA_SUELO_PCT, pct));

/**
 * Plazas reservadas para quien todavía no ha vivido ninguna experiencia, redondeadas
 * **hacia arriba** — con 15 plazas y 33 % son 5, no 4 ni 4.95 (documento 03b §7.3,
 * §10.1). Un aforo nulo es ilimitado (CL-4 del documento 03a): sin límite no hay nada que
 * reservar, así que devuelve 0.
 */
export const plazasReservadas = (capacity: number | null, pct: number): number => {
	if (capacity === null) {
		return 0;
	}
	return Math.ceil((capacity * reservaEfectivaPct(pct)) / RESERVA_TECHO_PCT);
};

// -----------------------------------------------------------------------------
// El enlace de organizador (documento 04 §6): los cinco resultados posibles de
// comprobar un enlace sin consumirlo.
// -----------------------------------------------------------------------------

export type ResultadoEnlaceOrganizador = 'ok' | 'wrong-user' | 'expired' | 'used' | 'not-found';

// -----------------------------------------------------------------------------
// El árbol de puertas (documento 03a §7.1; documento 03b §10.3): quién puede montar un
// plan. Se comprueba de izquierda a derecha y la primera puerta que salta manda — por
// eso alguien con un enlace roto ve lo del enlace, no lo de su rol (CA-03b.3).
// -----------------------------------------------------------------------------

/**
 * Las cinco puertas del asistente más el desenlace sin puerta ("formulario"). Pendiente y
 * grumete tienen puerta propia; miembro (`STANDARD`/`LITE`/`LEGACY`), colaborador y
 * administrador llegan al formulario.
 */
export type PuertaCreacionExperiencia =
	| 'sinSesion'
	| 'enlaceRoto'
	| 'pendiente'
	| 'grumete'
	| 'matriculaCaducada'
	| 'formulario';

/** Los seis niveles de `tier` (documento 02a §5.3, `contracts/users`), repetidos aquí para no acoplar `rules/` a `contracts/` (ADR-0017: `rules/` es terreno independiente). */
export type TierCreacionExperiencia = 'PENDING' | 'GRUMETE' | 'STANDARD' | 'LITE' | 'LEGACY' | 'COLLABORATOR';

export interface ContextoPuertaCreacionExperiencia {
	haySesion: boolean;
	/**
	 * Bloqueo por matrícula caducada (documento 03a §7.1: "🎫 matrícula caducada"). Sin
	 * fuente de datos hasta el documento 07a (membresía y pagos): llega siempre `false`
	 * hasta entonces — deliberado, no un descuido. Cuando el documento 07a aporte de
	 * dónde sale este valor, el único cambio necesario es en quien llama a esta función,
	 * nunca en esta firma.
	 */
	matriculaCaducada: boolean;
	/** Enlace que trajo la persona al asistente, si trajo alguno (documento 04 §6). Ausente = sin enlace. */
	resultadoEnlace?: ResultadoEnlaceOrganizador;
	systemRole: 'ADMIN' | 'USER';
	tier: TierCreacionExperiencia;
}

/**
 * Resuelve qué puerta corresponde, o `'formulario'` si ninguna bloquea. El orden es la
 * regla, no un detalle de implementación (CA-03b.3): primero si hay sesión, después el
 * enlace, después el rol. Dentro del rol, `systemRole = ADMIN` pasa siempre (la matriz de
 * permisos del documento 03a §8.1 marca `✅` en todo para Admin); si no es administrador,
 * la matrícula caducada manda sobre el tier concreto, porque es más específica que
 * "pendiente" o "grumete" — es la misma persona con un motivo de bloqueo distinto.
 */
export const resolverPuertaCreacion = (contexto: ContextoPuertaCreacionExperiencia): PuertaCreacionExperiencia => {
	if (!contexto.haySesion) {
		return 'sinSesion';
	}
	if (contexto.resultadoEnlace !== undefined && contexto.resultadoEnlace !== 'ok') {
		return 'enlaceRoto';
	}
	if (contexto.systemRole === 'ADMIN') {
		return 'formulario';
	}
	if (contexto.matriculaCaducada) {
		return 'matriculaCaducada';
	}
	if (contexto.tier === 'PENDING') {
		return 'pendiente';
	}
	if (contexto.tier === 'GRUMETE') {
		return 'grumete';
	}
	return 'formulario';
};

// -----------------------------------------------------------------------------
// Qué etiqueta de acceso resulta de la intención declarada, y quién puede pedir cada una
// (documento 03a §6.2, §7.1; documento 04 RN-5).
// -----------------------------------------------------------------------------

export interface ContextoEtiquetaAcceso {
	enlaceValido: boolean;
	quiereOficial: boolean;
	systemRole: 'ADMIN' | 'USER';
	tier: TierCreacionExperiencia;
}

const esColaboradorOAdmin = (contexto: Pick<ContextoEtiquetaAcceso, 'systemRole' | 'tier'>): boolean =>
	contexto.tier === 'COLLABORATOR' || contexto.systemRole === 'ADMIN';

export type EtiquetaAccesoResultante = 'MEMBERS_ONLY' | 'OFFICIAL' | 'PRIVATE';

/**
 * Qué `accessLabel` resulta de la intención declarada (documento 03a §6.2, §7.1):
 * colaborador o administrador que además marca "¿crear plan oficial?" → `OFFICIAL`; con
 * un enlace de organizador válido → `MEMBERS_ONLY`; en cualquier otro caso, `PRIVATE` —
 * el plan de un miembro nace privado (RN-2 del documento 03a) y necesita el enlace para
 * abrirse a la comunidad (RN-3). Un colaborador o administrador con enlace pero sin
 * marcar oficial también resulta `MEMBERS_ONLY`: el enlace abre la comunidad, nunca
 * convierte un plan en oficial por sí solo (documento 03a §7.1, ejemplo de Diego).
 */
export const etiquetaAccesoResultante = (contexto: ContextoEtiquetaAcceso): EtiquetaAccesoResultante => {
	if (esColaboradorOAdmin(contexto) && contexto.quiereOficial) {
		return 'OFFICIAL';
	}
	if (contexto.enlaceValido) {
		return 'MEMBERS_ONLY';
	}
	return 'PRIVATE';
};

export interface ContextoPuedeCrearConEtiqueta {
	enlaceValido: boolean;
	etiqueta: 'BRAND' | 'COLLABORATOR' | 'MEMBERS_ONLY' | 'OFFICIAL' | 'PRIVATE';
	systemRole: 'ADMIN' | 'USER';
	tier: TierCreacionExperiencia;
}

/** Motivos de rechazo — claves, no texto para humanos: los traduce el frontend (namespace `experiences`). */
export type MotivoNoPuedeCrear = 'estePlanNecesitaEnlace' | 'soloColaboradorCreaOficiales';

export interface ResultadoPuedeCrear {
	motivo?: MotivoNoPuedeCrear;
	permitido: boolean;
}

/**
 * Si el contexto puede crear una experiencia con la etiqueta pedida (documento 03a §6.2).
 * `OFFICIAL`, `COLLABORATOR` y `BRAND` exigen ser colaborador o administrador — las
 * etiquetas de colaborador y de marca no las cubre el §6.2 del PDF (que solo habla de
 * `OFFICIAL`/`MEMBERS_ONLY`/`PRIVATE`); se les aplica aquí el mismo requisito que a
 * `OFFICIAL` por decisión propia (decisiones.md), como pregunta abierta arrastrada.
 * `MEMBERS_ONLY` exige un enlace válido o ser colaborador o administrador. `PRIVATE` solo
 * exige sesión, que ya se comprobó antes de llegar aquí (la puerta `sinSesion` del árbol
 * de creación).
 */
export const puedeCrearConEtiqueta = (contexto: ContextoPuedeCrearConEtiqueta): ResultadoPuedeCrear => {
	const colaboradorOAdmin = esColaboradorOAdmin(contexto);
	if (contexto.etiqueta === 'OFFICIAL' || contexto.etiqueta === 'COLLABORATOR' || contexto.etiqueta === 'BRAND') {
		return colaboradorOAdmin ? { permitido: true } : { motivo: 'soloColaboradorCreaOficiales', permitido: false };
	}
	if (contexto.etiqueta === 'MEMBERS_ONLY') {
		return contexto.enlaceValido || colaboradorOAdmin
			? { permitido: true }
			: { motivo: 'estePlanNecesitaEnlace', permitido: false };
	}
	return { permitido: true };
};

// -----------------------------------------------------------------------------
// La matriz de visibilidad de la ficha de experiencia (documento 02b §8.3, aplicada aquí
// por decisión 6 de la iteración 02 — decisiones.md de esa iteración— y confirmada por
// CA-02b.8/9/10, que esta iteración cierra en pantalla).
// -----------------------------------------------------------------------------

/**
 * "Quién va" (documento 05a «la ficha redactada», fila `attendees`; documento 05b §10.6, "el
 * tablón"): nombre, avatar e icono de aportación — JAMÁS teléfono ni redes sociales
 * (CA-05b.10). Redeclarado aquí, no importado de `contracts/experienceJoins`
 * (`participantResponseSchema`), por la misma razón que el resto de este fichero (ADR-0017:
 * `rules/` es terreno independiente).
 */
export interface ParticipanteRedactable {
	avatarUrl: string | null;
	aventureroSubrole: string | null;
	fullName: string | null;
	id: string;
}

/**
 * "Contacto de participantes" (documento 05a «la ficha redactada»): lo mismo que
 * `ParticipanteRedactable`, más los dos canales de contacto directo — solo para quien
 * organiza o administra, nunca para quien solo tiene plaza.
 */
export interface ParticipanteContactoRedactable extends ParticipanteRedactable {
	instagram: string | null;
	whatsapp: string | null;
}

/**
 * Subconjunto de "experience" que puede redactarse según quién pregunta
 * (`GET /experiences/:id/detail`, documento 03a §6.3; documento 05a «la ficha redactada»).
 * `howToArrive`, `meetingPoint` y `whatsappGroupUrl` no son columnas propias: el mapper del
 * backend las extrae de "intakeMeta" antes de redactar, porque `redactarPorVisibilidad`
 * opera sobre una entidad ya aplanada, no sobre JSON anidado. `zone` tampoco es una columna
 * propia: el mapper la monta a partir de la relación `location` (`region`/`town`, hueco 3
 * detectado al cierre de la oleada 2 — `docs/estado-ejecucion.md`). `attendees` y
 * `participantContacts` tampoco son columnas: el mapper los monta a partir de
 * "experienceJoin" (iteración 05). `organizerFullName`/`organizerUsername` tampoco son
 * columnas: el mapper los monta a partir de la relación `user` (P-07, respuestas del cliente
 * 2026-09-17), el mismo camino que ya sigue `toExperienceListItemResponse` para el DTO del
 * catálogo. Ninguno se declara importado de `contracts/experiences` ni de
 * `contracts/experienceJoins`, para no acoplar `rules/` a `contracts/` (ADR-0017).
 */
export interface ExperienciaRedactable {
	adnHexPrimary: string | null;
	adnHexSecondary: string | null;
	askContribution: boolean;
	attendees: ParticipanteRedactable[];
	capacity: number | null;
	categoryId: string;
	confirmedCount: number;
	description: string | null;
	durationType: string;
	endDate: string | null;
	format: string;
	howToArrive: string | null;
	id: string;
	locationId: string | null;
	meetingPoint: string | null;
	organizerFullName: string | null;
	organizerUsername: string | null;
	participantContacts: ParticipanteContactoRedactable[];
	seatsLeft: number | null;
	startDate: string | null;
	title: string;
	whatsappGroupUrl: string | null;
	zone: { region: string | null; town: string | null } | null;
}

/**
 * La matriz de la especificación de esta iteración: título, descripción, fecha, zona,
 * plazas, datos de ADN, si se pregunta "¿qué puedes aportar?" (`askContribution`, documento
 * 05b §10.5) y cuántas plazas quedan (`seatsLeft`, documento 05b §10.3 — "el aforo se cuenta
 * en voz alta") visibles para cualquiera (`visitante`); punto de encuentro, cómo llegar y el
 * enlace del grupo de WhatsApp solo para quien tiene plaza u organiza o administra
 * (`apuntado`). "Quién va" (`attendees`) también es de quien tiene plaza (`apuntado`,
 * documento 05a «la ficha redactada» — antes inalcanzable hasta que "experienceJoin"
 * existiera, decisión 5 de decisiones.md de la iteración 03; esta iteración lo crea y esta
 * fila entra en vigor). "Contacto de participantes" (`participantContacts`) es exclusivo de
 * `organizadorOAdmin` — nunca de quien solo tiene plaza. "Notas internas del organizador" —la
 * única fila restante de la matriz del documento 02b §8.3— sigue sin columna ni tabla que
 * redactar: ningún documento leído hasta ahora la modela, así que sigue sin aparecer aquí.
 * `zone` sigue en `visitante` — CA-02b.8 exige que el visitante reciba «título, foto, zona, día y
 * plazas» —, pero `title`/`startDate`/`locationId` SUBEN a `conCuenta` (respuestas del cliente
 * 2026-09-17, P-13, «la miel en los labios»: sin cuenta el catálogo se ve tapado — se nota que
 * hay vida —, y lo único que se enseña a quien no tiene cuenta es el tipo/formato del plan, la
 * provincia aproximada (`zone`) y el mes, nunca el título exacto, la fecha concreta ni el sitio).
 * El catálogo sin sesión no usa este mapa en absoluto: usa su propio DTO mínimo,
 * `experienceCatalogPreviewResponseSchema` (`contracts/experiences`), calculado aparte por el
 * backend (el mes sale de `startDate`, pero nunca la fecha completa). Este mapa sigue
 * gobernando `GET /experiences/:id/detail`, que SIEMPRE exige sesión — de ahí que `conCuenta`,
 * no `visitante`, sea el visor mínimo correcto para el título y la fecha exacta: CA-02b.9 exige
 * que quien tiene cuenta sin plaza reciba «la zona y el día», nunca el punto exacto, y el título
 * completo entra en la misma franja de acceso que la fecha completa.
 *
 * `organizerFullName`/`organizerUsername` (P-07, respuestas del cliente 2026-09-17): «sin
 * cuenta NO se ve el sitio exacto, la fecha concreta, quién organiza, quién va, ni el enlace
 * del grupo» — visor mínimo `conCuenta`, nunca `visitante`, aunque la ficha de `/unirse/:id`
 * sea pública. Es el dato que cierra el defecto de la pantalla de cobro: para poder decir «el
 * pago se hace directamente con quien organiza, con su nombre delante» hace falta que ese
 * nombre viaje hasta ahí.
 */
export const MAPA_VISIBILIDAD_EXPERIENCIA: MapaVisibilidad<ExperienciaRedactable> = {
	adnHexPrimary: 'visitante',
	adnHexSecondary: 'visitante',
	askContribution: 'visitante',
	attendees: 'apuntado',
	capacity: 'visitante',
	categoryId: 'visitante',
	confirmedCount: 'visitante',
	description: 'visitante',
	durationType: 'visitante',
	endDate: 'visitante',
	format: 'visitante',
	howToArrive: 'apuntado',
	id: 'visitante',
	locationId: 'conCuenta',
	meetingPoint: 'apuntado',
	organizerFullName: 'conCuenta',
	organizerUsername: 'conCuenta',
	participantContacts: 'organizadorOAdmin',
	seatsLeft: 'visitante',
	startDate: 'conCuenta',
	title: 'conCuenta',
	whatsappGroupUrl: 'apuntado',
	zone: 'visitante',
};

// -----------------------------------------------------------------------------
// Las franjas de edad del asistente (documento 03c): una franja ancha anula a las estrechas
// que contiene.
// -----------------------------------------------------------------------------

/**
 * Las nueve franjas de edad (documento 02a §5.3, `contracts/users`: `AGE_RANGES`), repetidas
 * aquí para no acoplar `rules/` a `contracts/` (ADR-0017: `rules/` es terreno independiente).
 */
export type FranjaEdad =
	| 'R_18_23'
	| 'R_23_30'
	| 'R_18_30'
	| 'R_23_35'
	| 'R_30_35'
	| 'R_35_40'
	| 'R_40_50'
	| 'R_50_60'
	| 'R_60_PLUS';

/**
 * Deriva el rango numérico `[bajo, alto]` del propio nombre de la franja — `"R_18_23"` →
 * `[18, 23]`, `"R_60_PLUS"` → `[60, +∞)` — en vez de mantener una tabla escrita a mano que
 * pudiera desincronizarse de `AGE_RANGES`: el nombre de cada franja YA es su rango.
 */
const limitesFranjaEdad = (franja: FranjaEdad): readonly [number, number] => {
	const [, bajoTexto, altoTexto] = franja.split('_');
	const bajo = Number(bajoTexto);
	const alto = altoTexto === 'PLUS' ? Number.POSITIVE_INFINITY : Number(altoTexto);
	return [bajo, alto];
};

/**
 * Una franja ancha anula a las estrechas que contiene (documento 03c): `R_18_30` + `R_23_30`
 * guarda solo `R_18_30`, porque `R_23_30` queda contenida dentro de ella; `R_18_23` + `R_23_30`
 * guarda las dos, porque se solapan en el 23 pero ninguna es subconjunto de la otra. También
 * deduplica cualquier franja repetida en la entrada.
 */
export const normalizarFranjasEdad = (franjas: FranjaEdad[]): FranjaEdad[] => {
	const unicas = [...new Set(franjas)];
	return unicas.filter(franja => {
		const [bajo, alto] = limitesFranjaEdad(franja);
		return !unicas.some(otra => {
			if (otra === franja) {
				return false;
			}
			const [otroBajo, otroAlto] = limitesFranjaEdad(otra);
			const laContiene = otroBajo <= bajo && otroAlto >= alto;
			const esEstrictamenteMasAncha = laContiene && (otroBajo < bajo || otroAlto > alto);
			return esEstrictamenteMasAncha;
		});
	});
};

// -----------------------------------------------------------------------------
// Si cambiar las franjas de edad de un plan ya publicado dejaría fuera a alguien ya confirmado
// (respuestas del cliente 2026-09-17, P-33).
// -----------------------------------------------------------------------------

/**
 * Edad en años cumplidos de `nacimiento` a fecha `ahora`: se resta un año si el cumpleaños de
 * este año todavía no ha llegado. Ninguna función de este fichero llama a `Date.now()` ni
 * construye `new Date()` sin argumentos (mismo criterio que `rules/joins.ts`): la hora entra
 * siempre como parámetro, para que el resultado sea reproducible en un test con valores fijos.
 */
const edadEnAnios = (nacimiento: Date, ahora: Date): number => {
	const noHaLlegadoElCumpleanosEsteAnio =
		ahora.getMonth() < nacimiento.getMonth()
		|| (ahora.getMonth() === nacimiento.getMonth() && ahora.getDate() < nacimiento.getDate());
	const anioSinAjustar = ahora.getFullYear() - nacimiento.getFullYear();
	return noHaLlegadoElCumpleanosEsteAnio ? anioSinAjustar - 1 : anioSinAjustar;
};

/**
 * Si `edad` cae dentro de al menos una de `franjas` — el mismo criterio que ya cierra P-24 para
 * el filtro del catálogo («a una persona le sale el plan si su edad cae dentro de cualquiera de
 * las franjas declaradas. Nada de elegir una franja "ganadora"»), reutilizado aquí para decidir
 * si alguien queda dentro o fuera de un conjunto de franjas nuevo.
 */
const edadEncajaEnAlgunaFranja = (edad: number, franjas: readonly FranjaEdad[]): boolean =>
	franjas.some(franja => {
		const [bajo, alto] = limitesFranjaEdad(franja);
		return edad >= bajo && edad <= alto;
	});

/**
 * Si cambiar las franjas de edad de un plan a `franjasNuevas` es admisible (respuestas del
 * cliente 2026-09-17, P-33): «Las franjas de edad se abren a quien organiza, con una condición:
 * si ya hay alguien confirmado que se quedaría fuera de la franja nueva, no se cambia — se le
 * avisa de que tiene que hablar con esa persona primero. Nadie se queda fuera de un plan al que
 * ya le dijeron que sí». Admisible cuando TODAS las fechas de nacimiento de quien ya está
 * confirmado siguen encajando en alguna de las franjas nuevas; con cero confirmados, cualquier
 * cambio es admisible porque no hay nadie a quien proteger todavía. Esta función solo calcula la
 * admisibilidad — decidir si SE BLOQUEA la escritura o solo se avisa en pantalla es del servicio
 * que la llama, no de esta regla pura.
 */
export const puedeCambiarFranjasEdad = (
	franjasNuevas: readonly FranjaEdad[],
	fechasNacimientoConfirmados: readonly Date[],
	ahora: Date,
): boolean =>
	fechasNacimientoConfirmados.every(nacimiento =>
		edadEncajaEnAlgunaFranja(edadEnAnios(nacimiento, ahora), franjasNuevas),
	);

// -----------------------------------------------------------------------------
// El estado con el que nace una experiencia (documento 03c) — sustituye a la regla implícita
// de hoy, que nacía siempre en DRAFT.
// -----------------------------------------------------------------------------

/** Las cinco etiquetas de acceso (documento 03a §5.3, `contracts/experiences`), repetidas aquí por la misma razón que `TierCreacionExperiencia` (ADR-0017). */
export type EtiquetaAccesoExperiencia = 'BRAND' | 'COLLABORATOR' | 'MEMBERS_ONLY' | 'OFFICIAL' | 'PRIVATE';

/**
 * El estado con el que nace una experiencia según su etiqueta de acceso (documento 03c): todo
 * lo que va a llegar a la comunidad (`OFFICIAL`, `MEMBERS_ONLY`, `COLLABORATOR`, `BRAND`) nace
 * `PENDING_APPROVAL`, pendiente de revisión de administración; solo `PRIVATE` nace `PUBLISHED`
 * directamente, porque un plan privado no pasa por esa revisión. Ya no se nace en `DRAFT` al
 * terminar el asistente (decisión anterior de la iteración 3, revertida por el documento 03c):
 * `DRAFT` sigue siendo un estado válido de "experience" —lo usa el borrador guardado en
 * servidor antes de enviarse—, pero esta función nunca lo devuelve.
 */
export const estadoInicialExperiencia = (
	accessLabel: EtiquetaAccesoExperiencia,
): 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' => (accessLabel === 'PRIVATE' ? 'PUBLISHED' : 'PENDING_APPROVAL');

// -----------------------------------------------------------------------------
// Quién puede editar qué campo de una experiencia ya creada (documento 03c, P-11).
// -----------------------------------------------------------------------------

/**
 * Campos de "experience" que el organizador puede editar (documento 03c, P-11): los que recoge
 * el asistente y no comprometen el control de acceso. La fecha entra en esta lista —se puede
 * mover, con aviso en pantalla, decisión de UI que no vive en esta regla— y también el aforo,
 * pero solo en la dirección de subirlo: bajarlo por debajo de los ya confirmados exige conocer
 * el valor actual y el ya confirmado, dos datos que `puedeEditarCampo` no recibe porque no
 * decide sobre valores, decide sobre CAMPOS — esa comparación de valores es responsabilidad del
 * servicio que sí los tiene.
 */
export const CAMPOS_EDITABLES_ORGANIZADOR = [
	'title',
	'description',
	'material',
	'level',
	'meetingPoint',
	'howToArrive',
	'whatsappGroupUrl',
	'capacity',
	'startDate',
	'endDate',
] as const;

export type CampoExperiencia = (typeof CAMPOS_EDITABLES_ORGANIZADOR)[number] | 'accessLabel';

/**
 * Campos bloqueados para el organizador (documento 03c, P-11): la etiqueta de acceso decide qué
 * comunidad ve el plan, y esa decisión es exclusiva de administración.
 */
export const CAMPOS_PROTEGIDOS_EXPERIENCIA: readonly CampoExperiencia[] = ['accessLabel'];

/**
 * Si `quien` puede editar `campo` de una experiencia (documento 03c, P-11). Administración
 * edita cualquier campo declarado; el organizador, cualquiera salvo los de
 * `CAMPOS_PROTEGIDOS_EXPERIENCIA`.
 */
export const puedeEditarCampo = (campo: CampoExperiencia, quien: 'organizador' | 'admin'): boolean => {
	if (quien === 'admin') {
		return true;
	}
	return !CAMPOS_PROTEGIDOS_EXPERIENCIA.includes(campo);
};

// -----------------------------------------------------------------------------
// Quién puede GESTIONAR una experiencia entera (auditoría funcional 2026-09-10, B2/H-03):
// organiza o administra. Distinta de `puedeEditarCampo`, que decide campo a campo una vez ya
// se sabe que sí se gestiona.
// -----------------------------------------------------------------------------

export interface ContextoPuedeGestionarExperiencia {
	coordinatorUserId: string | null;
	systemRole: 'ADMIN' | 'USER';
	usuarioId: string;
}

/**
 * Si `usuarioId` puede GESTIONAR esta experiencia (auditoría funcional 2026-09-10, B2/H-03:
 * `GET /experiences/:id` devolvía cualquier experiencia a cualquier sesión). Cierto para
 * administración o para quien la organiza; un `coordinatorUserId` nulo (borrador sin
 * organizador) nunca coincide con nadie, porque `usuarioId` siempre es una cadena real —
 * la comparación de igualdad ya lo garantiza sin un caso especial. Quien no gestiona recibe
 * 404 neutro, nunca 403 (RN-7, mismo criterio que el resto del módulo).
 */
export const puedeGestionarExperiencia = (contexto: ContextoPuedeGestionarExperiencia): boolean =>
	contexto.systemRole === 'ADMIN' || contexto.coordinatorUserId === contexto.usuarioId;

// -----------------------------------------------------------------------------
// La reserva de novatos, quién la aplica y quién puede cambiar su porcentaje (documento 03c).
// -----------------------------------------------------------------------------

/**
 * Si esta etiqueta de acceso corresponde a una experiencia oficial, a efectos de negocio:
 * `OFFICIAL`, `COLLABORATOR` y `BRAND` (documento 03c, D-5). Es la misma lista que decide si
 * aplica la reserva de novatos (`aplicaReservaNovatos`, más abajo) y, desde la iteración 06, si
 * la experiencia pasa por el ciclo de solicitud del centro de mando — las dos preguntas
 * comparten hoy la misma lista por decisión D-5, cerrada por el documento 03c; tenerlas
 * duplicadas garantizaría que divergieran el día que una de las dos cambiara sin la otra.
 */
export const esExperienciaOficial = (accessLabel: EtiquetaAccesoExperiencia): boolean =>
	accessLabel === 'OFFICIAL' || accessLabel === 'COLLABORATOR' || accessLabel === 'BRAND';

/**
 * Si la reserva de plazas para novatos (`newcomerReservePct`/`reservaEfectivaPct`) aplica a
 * esta etiqueta de acceso (documento 03c): cierto para `OFFICIAL`, `COLLABORATOR` y `BRAND`;
 * falso para `MEMBERS_ONLY` y `PRIVATE`. Sustituye a la regla implícita de hoy, que solo la
 * aplicaba a `OFFICIAL`. Delega en `esExperienciaOficial`: es la misma pregunta de negocio,
 * no una regla propia de la reserva.
 */
export const aplicaReservaNovatos = (accessLabel: EtiquetaAccesoExperiencia): boolean =>
	esExperienciaOficial(accessLabel);

/**
 * Si se puede cambiar `newcomerReservePct` de `anterior` a `nuevo` (documento 03c, RN-6
 * ampliada): antes de publicar, cualquier valor vale; tras publicar, el organizador solo puede
 * subirlo o dejarlo igual, nunca bajarlo — administración sí puede bajarlo en cualquier
 * momento. El valor que esta función autoriza es el GUARDADO, en bruto: el suelo del 33 % se
 * sigue aplicando solo al calcular, con `reservaEfectivaPct` (que esta función no toca).
 */
export const puedeCambiarPorcentajeReserva = (
	anterior: number,
	nuevo: number,
	yaPublicada: boolean,
	quien: 'organizador' | 'admin',
): boolean => {
	if (!yaPublicada || quien === 'admin') {
		return true;
	}
	return nuevo >= anterior;
};

// -----------------------------------------------------------------------------
// La matrícula (documento 02c): hasta cuándo vale, y si ya caducó.
// -----------------------------------------------------------------------------

/**
 * Si la matrícula ya caducó (documento 02c, `user.membershipValidUntil`): compara la fecha con
 * `hoy` y nada más. `null` significa SIN matrícula —no se ha comprado nunca—, que no es lo
 * mismo que caducada: devuelve `false`. La matrícula vale HASTA `validUntil` inclusive: caduca
 * estrictamente después de esa fecha, no en ella.
 */
export const matriculaCaducada = (validUntil: Date | null, hoy: Date): boolean => {
	if (validUntil === null) {
		return false;
	}
	return validUntil.getTime() < hoy.getTime();
};

// -----------------------------------------------------------------------------
// La portada de una tarjeta de experiencia (documento 03c): cascada de cuatro niveles.
// -----------------------------------------------------------------------------

export interface EntradaResolverPortada {
	category: { colorHex: string; emoji: string };
	coverAdminUrl: string | null;
	coverOrganizerUrl: string | null;
	/**
	 * Foto del recuerdo asociado a esta experiencia (documento 03c). Todavía SIN FUENTE de
	 * datos —ningún documento leído hasta ahora modela de dónde sale—; parámetro opcional para
	 * cuando exista. Ausente o nula: ese nivel de la cascada no aporta portada y se sigue al
	 * siguiente.
	 */
	memoryPhotoUrl?: string | null;
}

export type OrigenPortada = 'admin' | 'categoria' | 'organizador' | 'recuerdo';

export interface PortadaResuelta {
	colorHex?: string;
	emoji?: string;
	origen: OrigenPortada;
	url?: string;
}

/**
 * La portada de una experiencia, cascada de cuatro niveles (documento 03c): la portada que
 * pone administración (`coverAdminUrl`) gana siempre que exista; si no, la foto del recuerdo
 * (`memoryPhotoUrl`); si tampoco, la portada que subió el organizador al crear
 * (`coverOrganizerUrl`); y si ninguna de las tres existe, el emoji y el color de la categoría —
 * el CUARTO NIVEL ES EL SUELO: ninguna tarjeta se queda nunca sin identidad visual.
 */
export const resolverPortada = (entrada: EntradaResolverPortada): PortadaResuelta => {
	if (entrada.coverAdminUrl) {
		return { origen: 'admin', url: entrada.coverAdminUrl };
	}
	if (entrada.memoryPhotoUrl) {
		return { origen: 'recuerdo', url: entrada.memoryPhotoUrl };
	}
	if (entrada.coverOrganizerUrl) {
		return { origen: 'organizador', url: entrada.coverOrganizerUrl };
	}
	return { colorHex: entrada.category.colorHex, emoji: entrada.category.emoji, origen: 'categoria' };
};

// -----------------------------------------------------------------------------
// La plantilla deducida del arranque rápido (documento 03c): a diferencia de
// `experienceTemplateResponseSchema` (calculada de una serie real completa), esta deduce
// campo a campo qué valores repite una tanda de ediciones.
// -----------------------------------------------------------------------------

export interface EdicionParaPlantilla {
	categoryId: string;
	durationType: string;
	/** Fecha de la edición (ISO), para desempatar por recencia cuando dos valores empatan. */
	fecha: string;
	format: string;
	nicheIds: readonly string[];
}

/**
 * Los cuatro campos deducibles. Deliberadamente SIN fechas, aforo, precio, gasto estimado ni
 * punto de encuentro (documento 03c): esos campos nunca se rellenan desde una plantilla, cada
 * edición tiene los suyos propios y copiarlos sería arrastrar un dato que ya no pertenece a la
 * experiencia nueva.
 */
export interface PlantillaDeducida {
	categoryId?: string;
	durationType?: string;
	format?: string;
	nicheIds?: readonly string[];
}

/**
 * El valor más repetido de un campo entre `ediciones`, o `undefined` si ninguno llega a la
 * mitad (documento 03c, reglas 1 y 2 de `calcularPlantillaDeducida`). `serializar` compara por
 * IGUALDAD DE VALOR, no de referencia — imprescindible para `nicheIds`, donde dos ediciones con
 * el mismo conjunto de nichos en arrays distintos deben contar como el mismo valor.
 */
const valorMasRepetido = <T>(
	ediciones: readonly EdicionParaPlantilla[],
	extraer: (edicion: EdicionParaPlantilla) => T,
	serializar: (valor: T) => string,
): T | undefined => {
	if (ediciones.length === 0) {
		return undefined;
	}
	const conteos = new Map<string, number>();
	for (const edicion of ediciones) {
		const clave = serializar(extraer(edicion));
		conteos.set(clave, (conteos.get(clave) ?? 0) + 1);
	}
	const maxConteo = Math.max(...conteos.values());
	// Regla 2: si ningún valor llega a la mitad de las ediciones, el campo se deja vacío.
	if (maxConteo < ediciones.length / 2) {
		return undefined;
	}
	// Regla 1: a igualdad gana la edición más reciente — se recorren las ediciones ordenadas de
	// más reciente a más antigua y se devuelve el valor de la primera cuyo conteo sea el máximo.
	const porRecencia = [...ediciones].sort((a, b) => b.fecha.localeCompare(a.fecha));
	for (const edicion of porRecencia) {
		const valor = extraer(edicion);
		if (conteos.get(serializar(valor)) === maxConteo) {
			return valor;
		}
	}
	return undefined;
};

const serializarTexto = (valor: string): string => valor;
const serializarNichos = (valor: readonly string[]): string => [...valor].sort().join(',');

/**
 * Deduce una plantilla de una tanda de ediciones (documento 03c, arranque rápido): para cada
 * uno de los cuatro campos (categoría, formato, duración, nichos) toma el valor más repetido
 * entre las ediciones, campo a campo y de forma independiente — el más repetido en categoría
 * puede venir de una tanda de ediciones distinta a la del más repetido en formato. Tres reglas,
 * las mismas para los cuatro campos: (1) a igualdad de repeticiones gana el valor de la edición
 * más reciente; (2) si ningún valor llega a la MITAD de las ediciones, el campo se deja vacío
 * en vez de forzar un valor poco representativo; (3) fechas, aforo, precio, gasto estimado y
 * punto de encuentro nunca se rellenan (no tienen campo en `PlantillaDeducida`).
 */
export const calcularPlantillaDeducida = (ediciones: readonly EdicionParaPlantilla[]): PlantillaDeducida => ({
	categoryId: valorMasRepetido(ediciones, edicion => edicion.categoryId, serializarTexto),
	durationType: valorMasRepetido(ediciones, edicion => edicion.durationType, serializarTexto),
	format: valorMasRepetido(ediciones, edicion => edicion.format, serializarTexto),
	nicheIds: valorMasRepetido(ediciones, edicion => edicion.nicheIds, serializarNichos),
});
